import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * SagoMax — Configuratore prodotto v2 (enhancement).
 *
 * Il configuratore funziona interamente senza JS (campi nativi, radio, file upload).
 * Questo modulo aggiunge comportamenti opzionali e sicuri:
 *  1) Ordini puliti: sull'evento `formdata` del product form rimuove ogni
 *     `properties[...]` vuota, così le proprietà bianche non arrivano mai all'ordine.
 *  2) Rami condizionali per modalità (data-mode: bianco|stampato|sagoma|scritta):
 *     - bianco: "Solo bianco" ⇄ "Bianco + stampa adesiva" (pannello grafica);
 *     - tutte: pannelli "Carica file" ⇄ "Libreria/Catalogo".
 *  3) Selettore design/soggetti: ricerca client-side, sync `properties[_Design ID]`,
 *     chip di selezione con rimozione.
 *  4) UX file: nome file selezionato + rimozione.
 *  5) Validazione inline (campo `data-sx-required`, es. testo della scritta):
 *     intercetta il submit in capture, blocca, mostra errore aria-live e focalizza.
 *  6) Riepilogo (`data-sx-recap`) aggiornato ad ogni scelta.
 *  7) Companion add "stampa adesiva" (solo se `data-adhesive-variant` è presente,
 *     cioè se il merchant ha configurato il prodotto dedicato nel blocco):
 *     dopo che il tema ha aggiunto la struttura al carrello (StandardEvents.
 *     cartLinesUpdate con esito positivo), aggiunge la stampa adesiva come riga
 *     separata via /cart/add.js con properties leggibili che la collegano alla
 *     struttura, poi dispatcha un CartLinesUpdateEvent con le sections aggiornate
 *     così drawer e icona carrello si aggiornano con il flusso standard del tema.
 *
 * Non calcola né altera MAI prezzi: le righe carrello usano i prezzi reali
 * delle varianti Shopify.
 */
class SagoMaxConfigurator extends HTMLElement {
  /** Marcatore per ignorare gli eventi cart generati da questo stesso modulo. */
  static COMPANION_SOURCE = 'sagomax-configurator';

  /** Dedup globale: una sola tornata di companion per add, anche se più istanze/
   *  listener si sono accumulati (il tema ri-renderizza la sezione al cambio variante). */
  static _lastCompanionAddAt = 0;

  connectedCallback() {
    this.form = this.dataset.form ? document.getElementById(this.dataset.form) : null;
    this.mode = this.dataset.mode || '';
    this.bindFormCleanup();
    this.bindPurchaseToggle();
    this.bindSourceToggle();
    this.bindLibrarySelects();
    this.bindSagomaPicker();
    this.bindFileUx();
    this.bindRequiredValidation();
    this.bindRequiredChoices();
    this.bindCustomShapeUpload();
    this.bindAreaCalculator();
    this.bindCompanionAdd();
    this.bindThemeMedia();
    this.bindMinQuantity();
    this.bindIntentGate();
    this.bindRequiredScope();
    this.updateRecap();
  }

  disconnectedCallback() {
    if (this.onCartLinesUpdate) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
    }
    if (this._minQtyAbort) this._minQtyAbort.abort();
  }

  // ── 1 · Ordini puliti ──────────────────────────────────────────────────────

  bindFormCleanup() {
    if (!this.form || this.form.dataset.sxConfBound === '1') return;
    this.form.dataset.sxConfBound = '1';
    this.form.addEventListener('formdata', (event) => {
      const fd = event.formData;
      for (const [key, value] of Array.from(fd.entries())) {
        if (!key.startsWith('properties[')) continue;
        if (typeof value === 'string') {
          if (value.trim() === '') fd.delete(key);
        } else if (value instanceof File) {
          if (value.size === 0 && !value.name) fd.delete(key);
        }
      }
    });
  }

  setGroupState(group, visible) {
    if (!group) return;
    group.hidden = !visible;
    group.querySelectorAll('input, select, textarea').forEach((el) => {
      if (el.hasAttribute('data-sx-lib-id')) return; // gestito da syncLibraryState
      el.disabled = !visible;
    });
  }

  // ── 2 · Modalità acquisto (bianco: solo ⇄ + stampa adesiva) ───────────────

  get adhesiveChosen() {
    const checked = this.querySelector('[data-sx-purchase]:checked');
    return Boolean(checked && checked.dataset.sxPurchase === 'adesiva');
  }

  bindPurchaseToggle() {
    const radios = this.querySelectorAll('[data-sx-purchase]');
    if (radios.length === 0) return;
    const panel = this.querySelector('[data-sx-adhesive-panel]');
    const soloNote = this.querySelector('[data-sx-solo-note]');
    const apply = () => {
      const adhesive = this.adhesiveChosen;
      this.setGroupState(panel, adhesive);
      if (soloNote) soloNote.hidden = adhesive;
      if (adhesive) this.applySource();
      this.updateRecap();
    };
    radios.forEach((r) => r.addEventListener('change', apply));
    apply();
  }

  // ── 2b · Sorgente grafica (file ⇄ libreria) ───────────────────────────────

  applySource() {
    const panels = this.querySelectorAll('[data-sx-panel]');
    const sources = this.querySelectorAll('[data-sx-source]');
    if (panels.length <= 1 || sources.length === 0) return; // un solo ramo: resta sempre visibile
    const checked = this.querySelector('[data-sx-source]:checked');
    const active = checked ? checked.dataset.sxSource : sources[0].dataset.sxSource;
    panels.forEach((panel) => {
      this.setGroupState(panel, panel.dataset.sxPanel === active);
    });
    this.syncLibraryState('design');
    this.syncLibraryState('pattern');
  }

  bindSourceToggle() {
    const radios = this.querySelectorAll('[data-sx-source]');
    if (radios.length === 0) return;
    radios.forEach((r) => r.addEventListener('change', () => {
      this.applySource();
      this.updateRecap();
    }));
    this.applySource();
  }

  // ── 3 · Tendine libreria / pattern con anteprima ──────────────────────────

  librarySelect(kind) {
    return this.querySelector(`[data-sx-lib-select="${kind}"]`);
  }

  syncLibraryState(kind) {
    const select = this.librarySelect(kind);
    if (!select) return;
    const store = this.querySelector(`[data-sx-lib-id="${kind}"]`);
    const panel = select.closest('[data-sx-panel]');
    const panelActive = !panel || !panel.hidden;
    const option = select.selectedOptions && select.selectedOptions[0];
    const hasChoice = Boolean(option && option.value && panelActive);

    if (store) {
      store.value = hasChoice ? option.dataset.designId || '' : '';
      store.disabled = !hasChoice;
    }

    const preview = this.querySelector(`[data-sx-lib-preview="${kind}"]`);
    if (preview) {
      const media = preview.querySelector('[data-sx-lib-preview-media]');
      const img = media ? media.querySelector('img') : null;
      const name = preview.querySelector('[data-sx-lib-preview-name]');
      const desc = preview.querySelector('[data-sx-lib-preview-desc]');
      if (hasChoice) {
        if (name) name.textContent = option.value;
        if (desc) desc.textContent = option.dataset.desc || '';
        if (media && img) {
          if (option.dataset.image) {
            img.src = option.dataset.image;
            img.alt = option.value;
            media.hidden = false;
          } else {
            img.removeAttribute('src');
            media.hidden = true;
          }
        }
        preview.hidden = false;
      } else {
        preview.hidden = true;
      }
    }
  }

  bindLibrarySelects() {
    ['design', 'pattern'].forEach((kind) => {
      const select = this.librarySelect(kind);
      if (!select) return;
      select.addEventListener('change', () => {
        this.syncLibraryState(kind);
        this.updateRecap();
      });
      this.syncLibraryState(kind);
    });
    this.preselectFromUrl();
  }

  /** Preselezione da galleria/vetrina: ?design=<handle-metaobject> oppure ?tema=<nome>. */
  preselectFromUrl() {
    const params = new URLSearchParams(window.location.search);
    if (this.preselectTheme(params.get('tema'))) return;
    const handle = params.get('design');
    if (!handle) return;
    for (const kind of ['design', 'pattern']) {
      const select = this.librarySelect(kind);
      if (!select) continue;
      const option = [...select.options].find((o) => o.dataset.handle === handle);
      if (!option) continue;
      select.value = option.value;
      const sourceRadio = this.querySelector(`[data-sx-source="${kind}"]`);
      if (sourceRadio) sourceRadio.checked = true;
      this.applySource();
      this.syncLibraryState(kind);
      this.updateRecap();
      return;
    }
  }

  /**
   * Tema arrivato dalla vetrina (hero della home, card set): ?tema=<nome tessera>.
   * Chi clicca «Matrimonio» li' dentro deve ritrovarlo gia' scelto qui, senza
   * ricercarlo a mano: spuntiamo la tessera, portiamo la sorgente su "tema" e la
   * segnaliamo per un istante. Confronto tollerante (accenti/spazi/maiuscole) cosi'
   * un nome scritto in modo leggermente diverso non fa cadere la preselezione.
   */
  preselectTheme(name) {
    if (!name || !name.trim()) return false;
    const wanted = this.normalizeName(name);
    if (!wanted) return false;
    const radios = Array.from(this.querySelectorAll('input[name="properties[Tema]"]'));
    const match = radios.find((r) => this.normalizeName(r.value) === wanted);
    if (!match) return false;
    match.checked = true;
    const sourceRadio = this.querySelector('[data-sx-source="theme"]');
    if (sourceRadio) sourceRadio.checked = true;
    this.applySource();
    this.updateRecap();
    const card = match.closest('.sx-themecard');
    if (card) card.classList.add('is-preselected');
    this.showThemeMedia(match.value);
    return true;
  }

  /** Confronto tollerante fra nomi di tema: accenti, maiuscole e spazi doppi non contano. */
  normalizeName(value) {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  /**
   * Porta in primo piano la FOTO del tema scelto.
   *
   * I temi SONO le foto del prodotto (il nome e' la parte dell'alt prima del trattino
   * lungo): «tema scelto» e «foto in evidenza» devono quindi essere la stessa cosa. Chi
   * clicca Natale nella vetrina deve trovarsi davanti la scenografia di Natale, non quella
   * della prima foto.
   *
   * La galleria di Horizon ha due volti e vanno serviti entrambi:
   * · da desktop e' una griglia dove la PRIMA tessera e' quella grande (regola CSS
   *   `.media-gallery--large-first-image .product-media-container:first-child`), quindi la
   *   tessera del tema viene portata in testa: diventa lei la foto grande;
   * · da telefono e' uno slideshow, quindi si seleziona la slide corrispondente.
   * Se quella foto non esiste non succede nulla: la galleria resta com'era.
   */
  showThemeMedia(name) {
    const wanted = this.normalizeName(name);
    if (!wanted) return;
    const scope = this.closest('.shopify-section') || document;
    const gallery = scope.querySelector('media-gallery') || document.querySelector('media-gallery');
    if (!gallery) return;

    const matches = (el) => {
      const img = el.querySelector('img[alt]');
      if (!img || !img.alt) return false;
      return this.normalizeName(img.alt.split('\u2014')[0]) === wanted;
    };

    // Desktop: la tessera del tema diventa la prima della griglia, cioe' quella grande.
    // NB: i pulsanti zoom portano il proprio indice scritto nel markup, quindi riordinare
    // le tessere non li disallinea.
    const grid = gallery.querySelector('.media-gallery__grid');
    if (grid) {
      const tile = Array.from(grid.children).find(matches);
      if (tile && tile !== grid.firstElementChild) grid.prepend(tile);
    }

    // Telefono: si seleziona la slide. Allo sbarco lo slideshow puo' non essere ancora
    // inizializzato (select() esce a vuoto finche' non ha il suo scroller), quindi si
    // riprova per qualche fotogramma invece di rinunciare al primo colpo.
    let tries = 0;
    const selectSlide = () => {
      const slideshow = gallery.slideshow;
      const slides = slideshow && slideshow.slides;
      if (!slides || slides.length === 0) {
        if (tries++ < 30) requestAnimationFrame(selectSlide);
        return;
      }
      const index = slides.findIndex(matches);
      // >= 0, non > 0: anche la PRIMA slide va selezionata, perche' quella corrente puo'
      // essere un'altra (si torna a Matrimonio dopo aver guardato Natale). Se e' gia'
      // quella giusta ci pensa select() a non fare nulla.
      if (index >= 0) slideshow.select(index, undefined, { animate: false });
    };
    selectSlide();
  }

  // ── 4a · «Qualcosa c'e'» — l'obbligo di dire cosa stampiamo ────────────────

  /**
   * Nulla che venga STAMPATO puo' finire in carrello senza che il cliente abbia detto
   * cosa stampare. Ma il negozio promette «elaborazione grafica sempre inclusa»: non si
   * puo' pretendere un file, altrimenti si respinge proprio chi compra per quel servizio.
   * Quindi la regola e': basta UNA qualsiasi di queste strade — file, link, trama, tema,
   * soggetto, nome, testo — oppure due righe scritte nel campo sentinella.
   *
   * L'obbligo vive sulla VALIDAZIONE NATIVA del browser, non su un listener submit: il
   * tema intercetta il submit su document in capture e aggiunge al carrello prima di
   * qualsiasi guardia nostra. La constraint validation invece gira prima ancora che
   * l'evento parta — ed e' l'unico punto in cui un blocco e' davvero un blocco.
   */
  bindIntentGate() {
    const sentinel = this.querySelector('[data-sx-intent]');
    if (!sentinel || !this.form) return;
    const help = this.querySelector('[data-sx-intent-help]');
    const MSG =
      'Dicci cosa mettere sulla grafica: basta una riga \u2014 oppure carica un file, scegli una trama o un tema.';

    // Un controllo dentro un pannello spento non deve mai bloccare: e' la regola che
    // tiene acquistabile «Solo bianco», dove di grafica non si parla proprio.
    const spento = (el) => !el || el.disabled || !!el.closest('[hidden]');
    const vivo = (el) => el && !spento(el);
    const pieno = (el, min) => vivo(el) && String(el.value || '').trim().length >= (min || 1);

    const altroCanale = () => {
      const file = this.querySelector('[data-sx-file]');
      if (vivo(file) && file.files && file.files.length > 0) return true;
      const link = this.querySelector('input[name="properties[Link file]"]');
      if (vivo(link) && link.value.trim().length >= 8 && link.value.indexOf('.') > -1) return true;
      if (pieno(this.querySelector('[data-sx-lib-select="design"]'))) return true;
      if (pieno(this.querySelector('[data-sx-lib-select="pattern"]'))) return true;
      const tema = this.querySelector('input[name="properties[Tema]"]:checked');
      if (vivo(tema)) return true;
      const sag = this.querySelector('[data-sx-sag-value]');
      if (sag && String(sag.value || '').trim() !== '') return true;
      if (pieno(this.querySelector('[data-sx-sag-freetext]'), 3)) return true;
      if (pieno(this.querySelector('input[name="properties[Testo della scritta]"]'))) return true;
      if (pieno(this.querySelector('input[name="properties[Nome / festeggiato]"]'), 3)) return true;
      if (pieno(this.querySelector('input[name="properties[Testo da inserire]"]'), 3)) return true;
      return false;
    };

    const sync = () => {
      // Sul ramo «Solo bianco» la sezione grafica e' nascosta: qui la sentinella si
      // disabilita, cosi' esce dalla validazione E dall'ordine (nessuna property vuota).
      const nascosta = !!sentinel.closest('[hidden]');
      sentinel.disabled = nascosta;
      if (nascosta) return;

      const ok = altroCanale();
      sentinel.required = !ok;
      if (ok) sentinel.setCustomValidity('');
      if (!help) return;
      help.textContent = ok
        ? 'Ci hai gi\u00e0 detto cosa serve. Qui puoi aggiungere altri dettagli, se ti va.'
        : 'Basta una riga: al resto pensiamo noi, e la grafica \u00e8 sempre inclusa. Se carichi un file, scegli una trama o un tema, questo campo si libera da solo.';
    };

    // Il messaggio nativo lo scriviamo noi: «Compila questo campo» non dice al cliente
    // che ha altre tre strade per sbloccarsi.
    sentinel.addEventListener('invalid', () => {
      if (!sentinel.value.trim()) sentinel.setCustomValidity(MSG);
    });
    sentinel.addEventListener('input', () => sentinel.setCustomValidity(''));

    // Ogni scelta nel configuratore puo' cambiare la risposta: si ricalcola su tutto.
    this.addEventListener('change', sync);
    this.addEventListener('input', sync);
    sync();
    this.sxSyncIntent = sync;
  }

  // ── 4a-bis · Un obbligo vale solo dove il cliente puo' rispondere ──────────

  /**
   * Regola unica, e non negoziabile: un controllo `required` dentro un pannello spento
   * deve essere `disabled`. Due motivi, entrambi gravi visti dal vivo:
   *
   * · il browser non riesce a mostrare l'errore su un campo che non puo' mettere a fuoco:
   *   il form semplicemente NON parte, senza un messaggio. Il cliente preme «Aggiungi»
   *   e non succede niente — il modo peggiore di perdere un ordine;
   * · l'obbligo non ha senso: se ha scelto «carica la tua grafica», il tema non lo
   *   riguarda.
   *
   * Tocchiamo solo cio' che abbiamo spento noi (`data-sx-auto-off`), per non litigare
   * con setGroupState, che disabilita di suo quando cambia la sorgente.
   *
   * Caso a parte, e il piu' pericoloso di tutti: la SAGOMA scelta dal catalogo. Li' il
   * soggetto vive nell'hidden canonico, mentre il campo libero resta vuoto — e se
   * restasse obbligatorio nessuna sagoma del catalogo sarebbe acquistabile.
   */
  bindRequiredScope() {
    const gestiti = () =>
      Array.from(
        this.querySelectorAll(
          '[data-sx-choice], [data-sx-sag-freetext], [data-sx-area-base], [data-sx-area-height], [data-sx-intent]'
        )
      );

    const sync = () => {
      gestiti().forEach((el) => {
        const spento = !!el.closest('[hidden]');
        if (spento && !el.disabled) {
          el.disabled = true;
          el.setAttribute('data-sx-auto-off', '');
        } else if (!spento && el.hasAttribute('data-sx-auto-off')) {
          el.disabled = false;
          el.removeAttribute('data-sx-auto-off');
        }
      });

      // Sagoma dal catalogo: il soggetto c'e' gia', il campo libero si fa da parte.
      // Su misura vale lo stesso se ha allegato una foto o un link: la foto E' il soggetto.
      // Pretendere anche la descrizione rimandava al campo a ogni «Aggiungi» un cliente
      // che aveva gia' caricato l'immagine (segnalato dal vivo, 2026-10-14).
      const canonico = this.querySelector('[data-sx-sag-value]');
      const libero = this.querySelector('[data-sx-sag-freetext]');
      if (canonico && libero) {
        const allegato = Array.from(this.querySelectorAll('[data-sx-sag-upload]')).some((el) => {
          if (el.disabled || el.closest('[hidden]')) return false;
          if (el.type === 'file') return !!(el.files && el.files.length > 0);
          const v = String(el.value || '').trim();
          return v.length >= 8 && v.indexOf('.') > -1;
        });
        const libera = String(canonico.value || '').trim() !== '' || allegato;
        libero.required = !libera;
        if (libera) libero.setCustomValidity('');
      }
    };

    this.addEventListener('change', sync);
    this.addEventListener('input', sync);
    // Il picker sagome scrive l'hidden da JS, senza eventi: lo osserviamo.
    const canonico = this.querySelector('[data-sx-sag-value]');
    if (canonico && window.MutationObserver) {
      new MutationObserver(sync).observe(canonico, { attributes: true, attributeFilter: ['value'] });
    }
    sync();
    this.sxSyncScope = sync;
  }

  // ── 4b · Minimo d'ordine per variante ──────────────────────────────────────

  /**
   * Alcune misure si producono solo in lotto («h 20 cm — ordine minimo 6 pezzi»).
   *
   * Invece di bloccare al click — e mandare via un cliente con un errore — si porta il
   * selettore quantita' al minimo APPENA quella misura viene scelta: chi preme «Aggiungi»
   * trova gia' il numero giusto e non deve capire nulla. Il blocco resta comunque, ma come
   * rete: si alza l'attributo `min` dell'input nativo del tema, e la validazione del
   * browser scatta PRIMA del submit (l'unico punto affidabile: il tema intercetta il
   * submit su document in capture, quindi qualsiasi guardia nostra arriverebbe dopo).
   *
   * La regola si legge dal titolo della variante via `data-minqty` (mappa id→minimo).
   */
  bindMinQuantity() {
    // Formato «id:minimo,id:minimo»: il JSON con le graffe faceva rifiutare il file
    // Liquid da Shopify (vedi commento nel blocco). Due righe e siamo pari.
    const raw = (this.dataset.minqty || '').trim();
    if (!raw || !this.form) return;
    const map = {};
    raw.split(',').forEach((pair) => {
      const [id, n] = pair.split(':');
      const min = parseInt(n, 10);
      if (id && min > 1) map[id.trim()] = min;
    });
    if (!Object.keys(map).length) return;

    const qty = this.form.querySelector('input[name="quantity"]:not([type="hidden"])');
    if (!qty) return;

    const note = this.querySelector('[data-sx-minqty-note]');
    const currentVariantId = () => {
      const picked = this.form.querySelector('input[name="id"], select[name="id"]');
      return picked ? String(picked.value || '') : '';
    };

    // Ricordiamo il numero che abbiamo messo NOI: serve per disfarlo quando il cliente
    // passa a una misura senza minimo. Senza questo, chi guarda la h 20 (minimo 6) e poi
    // sceglie la 100×100 si ritroverebbe SEI sagome grandi nel carrello — un addebito che
    // non ha mai chiesto. Se invece il numero l'ha scritto lui, non lo tocchiamo.
    let nostro = 0;

    const apply = (variantId = currentVariantId()) => {
      const min = map[variantId];
      const cambia = (valore) => {
        qty.value = String(valore);
        // Il selettore quantita' del tema e' un componente: va avvisato, altrimenti
        // i suoi pulsanti +/- continuerebbero a ragionare sul valore vecchio.
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (min && min > 1) {
        qty.min = String(min);
        // Anche se il numero l'avevamo messo noi per un'altra misura: dalla h 20 (6) alla
        // h 30 (5) si scende a 5, non si resta a 6 pagandone uno che nessuno ha chiesto.
        if (!qty.value || Number(qty.value) < min || (nostro && Number(qty.value) === nostro)) {
          cambia(min);
          nostro = min;
        }
        if (note) {
          note.textContent =
            'Questa misura si produce in lotto: il minimo e\u2019 di ' + min + ' pezzi, e li abbiamo gia\u2019 messi nel conteggio.';
          note.hidden = false;
        }
      } else {
        qty.min = '1';
        if (nostro && Number(qty.value) === nostro) {
          cambia(1);
          nostro = 0;
        }
        if (note) note.hidden = true;
      }
    };

    apply();
    // Cambio misura: il tema NON ricostruisce questo blocco e riscrive l'`id` del form da
    // codice, senza eventi. Ascoltare il `change` del form non bastava: i pulsanti misura
    // si chiamano «Dimensione-…» e stanno fuori dal form, quindi il minimo della h 20 (la
    // misura di partenza) restava addosso anche a chi sceglieva la 50×50 — 6 pezzi nel
    // carrello. Si ascolta l'annuncio di variante del tema, come fa local-pickup.js, e
    // l'id si prende da li'. Il setTimeout lascia passare prima product-form.js, che sullo
    // stesso annuncio rimette il `min` della quantita' a quello di Shopify (1).
    if (this._minQtyAbort) this._minQtyAbort.abort();
    this._minQtyAbort = new AbortController();
    const scope = this.closest('.shopify-section') || document;

    // Misura scelta adesso, letta dal pulsante (porta gia' l'id variante): non dipende
    // dalla risposta del server.
    const misuraScelta = () => {
      const r = scope.querySelector('input[type="radio"][data-variant-id]:checked');
      return r && !r.closest('product-card') ? String(r.dataset.variantId || '') : '';
    };

    // Al TOCCO, non alla risposta del server. Su rete lenta il tema impiega secondi a
    // ricaricare la variante: nel frattempo il cliente vedeva la 150×100 con la nota
    // «minimo 6» della h 20, e se premeva «Aggiungi» il tema metteva in coda l'aggiunta
    // con la quantita' di quel momento, senza validazione: 1 pezzo della h 30 nel
    // carrello (segnalato dal vivo, 2026-10-14).
    scope.addEventListener(
      'change',
      (event) => {
        const r = event.target;
        if (!(r instanceof HTMLInputElement) || !r.checked || !r.dataset.variantId) return;
        if (r.closest('product-card')) return;
        apply(String(r.dataset.variantId));
      },
      { signal: this._minQtyAbort.signal }
    );

    scope.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        if (event.target instanceof Element && event.target.closest('product-card')) return;
        if (!event.promise) return;
        event.promise
          .then(({ detail }) => {
            if (!detail || !detail.resource) return;
            const id = String(detail.resource.id);
            // Risposta di una misura che il cliente ha gia' lasciato: non deve riscrivere
            // nota e quantita' di quella scelta adesso.
            const ora = misuraScelta();
            if (ora && ora !== id) return;
            setTimeout(() => apply(id), 0);
          })
          .catch(() => {
            /* richiesta di variante annullata: arrivera' la successiva */
          });
      },
      { signal: this._minQtyAbort.signal }
    );
  }

  /** Cambiare tema nel personalizzatore cambia anche la foto in evidenza: una scelta sola. */
  bindThemeMedia() {
    const radios = Array.from(this.querySelectorAll('input[name="properties[Tema]"]'));
    if (radios.length === 0) return;
    radios.forEach((radio) =>
      radio.addEventListener('change', () => {
        if (radio.checked) this.showThemeMedia(radio.value);
      })
    );
  }

  // ── 3b · Picker Sagome — il cuore di SagoMax (modalità sagoma) ────────────

  /**
   * Contact-sheet delle 105 sagome nel buy-box: switch catalogo/su-misura,
   * ricerca, chip-famiglia, tessere selezionabili, scheda "Hai scelto",
   * 106ª cella su-misura, preselezione ?soggetto=.
   *
   * UN SOLO valore in uscita = properties[Soggetto scelto]:
   *  · l'hidden [data-sx-sag-value] riceve QUI il name (unico portatore, sempre);
   *  · al free-text [data-sx-sag-freetext] viene tolto il name (portava il no-JS).
   * Senza JS: is-enhanced non viene aggiunta → il CSS mostra solo il free-text,
   *  che conserva il suo name → una sola riga, come oggi.
   */
  bindSagomaPicker() {
    if (this.mode !== 'sagoma') return;
    const root = this.querySelector('.sx-sag');
    // Guard di rientro: connectedCallback può rifirare sulla stessa istanza (il tema
    // ri-attacca il buy-box al cambio variante). is-enhanced marca il già-fatto → una
    // seconda passata verrebbe qui a duplicare le chip e impilare i listener.
    if (!root || root.classList.contains('is-enhanced')) return;
    root.classList.add('is-enhanced');

    const hidden = root.querySelector('[data-sx-sag-value]');
    const search = root.querySelector('[data-sx-sag-search]');
    const countEl = root.querySelector('[data-sx-sag-count]');
    const chipsWrap = root.querySelector('[data-sx-sag-chips]');
    const emptyEl = root.querySelector('[data-sx-sag-empty]');
    const chosen = root.querySelector('[data-sx-sag-chosen]');
    const chosenImg = root.querySelector('[data-sx-sag-chosen-img]');
    const chosenName = root.querySelector('[data-sx-sag-chosen-name]');
    const freetext = root.querySelector('[data-sx-sag-freetext]');
    const pager = root.querySelector('.sx-sag__pager');
    const moreWrap = root.querySelector('.sx-sag__more');
    const moreBtn = root.querySelector('[data-sx-sag-more]');
    const picks = Array.from(root.querySelectorAll('[data-sx-sag-pick]'));
    const cells = Array.from(root.querySelectorAll('.sx-sag__cell:not(.sx-sag__cell--custom)'));
    const modeRadios = Array.from(root.querySelectorAll('[data-sx-sag-mode]'));
    const panels = Array.from(root.querySelectorAll('[data-sx-sag-panel]'));
    const uploads = Array.from(root.querySelectorAll('[data-sx-sag-upload]'));
    // L'allegato vive solo nel ramo su-misura: fuori da lì gli input sono disabilitati (non inviano).
    const setUploads = (on) => uploads.forEach((el) => { el.disabled = !on; });
    setUploads(false);

    const norm = (s) => (s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
    const famOf = (s) => (s || '').replace(/[\s–—-]*\d+$/, '').trim();

    // Hidden = unico portatore di properties[Soggetto scelto]; free-text neutralizzato.
    if (hidden) hidden.setAttribute('name', 'properties[Soggetto scelto]');
    if (freetext) freetext.removeAttribute('name');

    picks.forEach((r) => { r.dataset.norm = norm(r.value); });

    // ── Collasso a 18 tessere (la 106ª "il tuo soggetto" resta sempre visibile) ──
    const COLLAPSE = 18;
    let collapsed = false;
    if (pager && cells.length > COLLAPSE) {
      pager.classList.add('is-collapsed');
      collapsed = true;
      if (moreWrap) moreWrap.hidden = false;
    }
    const expand = () => {
      if (pager) pager.classList.remove('is-collapsed');
      collapsed = false;
      if (moreWrap) moreWrap.hidden = true;
    };
    if (moreBtn) moreBtn.addEventListener('click', expand);

    // ── Filtro (ricerca + chip) ──
    const applyFilter = () => {
      const q = norm(search ? search.value : '');
      const activeChip = chipsWrap ? chipsWrap.querySelector('.sx-sag__chip.is-active') : null;
      const fam = activeChip ? norm(activeChip.dataset.fam || '') : '';
      let n = 0;
      cells.forEach((cell) => {
        const pick = cell.querySelector('[data-sx-sag-pick]');
        const nm = pick ? pick.dataset.norm : '';
        let show = true;
        if (fam) show = nm.indexOf(fam) === 0;
        if (show && q) show = nm.indexOf(q) !== -1;
        cell.hidden = !show;
        if (show) n += 1;
      });
      if ((q || fam) && collapsed) expand();
      if (countEl) countEl.textContent = (q || fam) ? (n + (n === 1 ? ' risultato' : ' risultati')) : '105 soggetti pronti';
      if (emptyEl) emptyEl.hidden = n !== 0;
    };
    if (search) search.addEventListener('input', applyFilter);

    // ── Chip-famiglia (solo gruppi ≥2 elementi) ──
    if (chipsWrap) {
      const groups = {};
      picks.forEach((r) => { const f = famOf(r.value); if (f) groups[f] = (groups[f] || 0) + 1; });
      const fams = Object.keys(groups).filter((f) => groups[f] >= 2).sort((a, b) => groups[b] - groups[a]).slice(0, 8);
      const mkChip = (fam, label, active) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sx-sag__chip' + (active ? ' is-active' : '');
        b.dataset.fam = fam;
        b.textContent = label;
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
        b.addEventListener('click', () => {
          chipsWrap.querySelectorAll('.sx-sag__chip').forEach((c) => {
            const on = c === b;
            c.classList.toggle('is-active', on);
            c.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          applyFilter();
        });
        return b;
      };
      chipsWrap.appendChild(mkChip('', 'Tutti', true));
      fams.forEach((f) => chipsWrap.appendChild(mkChip(f, f, false)));
    }

    // ── Scheda "Hai scelto" ──
    const setChosen = (pick) => {
      if (!chosen) return;
      if (chosenImg) { chosenImg.src = pick.dataset.img || ''; chosenImg.alt = pick.value; }
      if (chosenName) chosenName.textContent = pick.value;
      chosen.hidden = false;
    };

    // ── Selezione tessera ──
    picks.forEach((r) => r.addEventListener('change', () => {
      if (!r.checked) return;
      if (hidden) hidden.value = r.value;
      setChosen(r);
      this.updateRecap();
    }));

    // ── Switch catalogo / su-misura ──
    const switchMode = (v) => {
      panels.forEach((p) => { p.hidden = p.dataset.sxSagPanel !== v; });
      if (v === 'catalogo') {
        const checked = root.querySelector('[data-sx-sag-pick]:checked');
        if (hidden) hidden.value = checked ? checked.value : '';
        if (chosen) chosen.hidden = !checked;
      } else {
        if (hidden) hidden.value = freetext ? freetext.value : '';
        if (chosen) chosen.hidden = true;
      }
      setUploads(v === 'sumisura');
      // Il cambio di ramo da tessera o link non emette eventi: gli obblighi si
      // ricalcolano qui, subito, senza aspettare l'observer sull'hidden.
      if (this.sxSyncScope) this.sxSyncScope();
      this.updateRecap();
    };
    modeRadios.forEach((r) => r.addEventListener('change', () => { if (r.checked) switchMode(r.value); }));

    // ── Free-text (ramo su-misura) ──
    if (freetext) freetext.addEventListener('input', () => {
      const active = root.querySelector('[data-sx-sag-mode]:checked');
      if (active && active.value === 'sumisura' && hidden) {
        hidden.value = freetext.value;
        this.updateRecap();
      }
    });

    // ── "Proponi il tuo" / 106ª cella → ramo su-misura ──
    root.querySelectorAll('[data-sx-sag-tocustom]').forEach((b) => b.addEventListener('click', () => {
      const r = root.querySelector('[data-sx-sag-mode="sumisura"]');
      if (r) r.checked = true;
      switchMode('sumisura');
      if (freetext) { try { freetext.focus(); } catch (e) { /* noop */ } }
    }));

    // ── "Cambia" → torna al muro ──
    const changeBtn = root.querySelector('[data-sx-sag-change]');
    if (changeBtn) changeBtn.addEventListener('click', () => {
      if (collapsed) expand();
      if (search) { try { search.focus(); } catch (e) { /* noop */ } }
      if (pager) pager.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    // ── Preselezione ?soggetto= (sostituisce il vecchio script inline) ──
    try {
      const param = new URLSearchParams(window.location.search).get('soggetto');
      if (param) {
        const match = picks.find((r) => r.dataset.norm === norm(param));
        if (match) {
          expand();
          match.checked = true;
          if (hidden) hidden.value = match.value;
          setChosen(match);
          const cat = root.querySelector('[data-sx-sag-mode="catalogo"]');
          if (cat) cat.checked = true;
          switchMode('catalogo');
          const cell = match.closest('.sx-sag__cell');
          if (cell) cell.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          const su = root.querySelector('[data-sx-sag-mode="sumisura"]');
          if (su) su.checked = true;
          if (freetext) freetext.value = param;
          switchMode('sumisura');
        }
      }
    } catch (e) { /* noop */ }
  }

  // ── 4 · UX file: nome + rimozione ─────────────────────────────────────────

  bindFileUx() {
    this.bindFileSizeGuard();
    const input = this.querySelector('[data-sx-file]');
    if (!input) return;
    const chip = this.querySelector('[data-sx-file-chip]');
    const name = this.querySelector('[data-sx-file-name]');
    const clear = this.querySelector('[data-sx-file-clear]');
    const apply = () => {
      const file = input.files && input.files[0];
      if (chip && name) {
        name.textContent = file ? file.name : '';
        chip.hidden = !file;
      }
      this.updateRecap();
    };
    input.addEventListener('change', apply);
    if (clear) {
      clear.addEventListener('click', () => {
        input.value = '';
        // Evento vero, non solo apply(): i controlli che dipendono dal file (sagoma
        // personalizzata, «qualcosa c'e'») devono sapere che il file non c'e' piu'.
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.focus();
      });
    }
  }

  /**
   * Shopify rifiuta un'aggiunta al carrello oltre 20 MB in tutto (413, misurato: 19,9 MB
   * passa, 20 no) e il tema non lo diceva: il cliente caricava la grafica dello striscione,
   * premeva «Aggiungi» e trovava il carrello vuoto. Qui il file troppo pesante si toglie
   * appena scelto, si spiega perche' e si porta il cliente sul campo link: l'ordine resta
   * possibile, la grafica arriva da WeTransfer/Drive o dopo l'ordine. Il margine sotto i
   * 20 MB e' per gli altri campi, che viaggiano nella stessa richiesta.
   */
  bindFileSizeGuard() {
    const LIMITE_MB = 19;
    const mb = new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 });
    this.querySelectorAll('[data-sx-file]').forEach((input) => {
      if (input.dataset.sxSizeGuard === '1') return;
      input.dataset.sxSizeGuard = '1';
      const field = input.closest('.sx-field');
      const box = field ? field.querySelector('[data-sx-file-err]') : null;
      const link = field ? field.querySelector('input[type="url"]') : null;
      // Registrato prima degli altri listener del file: quando il campo viene svuotato
      // qui, chip, recap e «qualcosa c'e'» vedono gia' che il file non c'e'.
      input.addEventListener('change', () => {
        const file = input.files && input.files[0];
        if (!file) return;
        if (file.size <= LIMITE_MB * 1024 * 1024) {
          if (box) box.hidden = true;
          return;
        }
        input.value = '';
        if (box) {
          box.textContent =
            '«' + file.name + '» pesa ' + mb.format(file.size / 1048576) + ' MB: qui si allegano file fino a ' +
            LIMITE_MB + ' MB. Incolla un link (WeTransfer, Drive, Canva) qui sotto, oppure mandacelo dopo l’ordine.';
          box.hidden = false;
        }
        if (link) link.focus();
      });
    });
  }

  // ── 5 · Validazione inline (campo obbligatorio, es. testo scritta) ────────

  showError(message) {
    const box = this.querySelector('[data-sx-error]');
    if (!box) return;
    box.textContent = message;
    box.hidden = false;
  }

  hideError() {
    const box = this.querySelector('[data-sx-error]');
    if (!box) return;
    box.hidden = true;
    box.textContent = '';
  }

  bindRequiredValidation() {
    const req = this.querySelector('[data-sx-required]');
    if (!req || !this.form) return;
    this.form.addEventListener(
      'submit',
      (event) => {
        if (req.disabled) return;
        if (req.value.trim() !== '') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError(req.dataset.sxRequiredMessage || 'Completa il campo obbligatorio prima di aggiungere al carrello.');
        req.setAttribute('aria-invalid', 'true');
        req.focus();
      },
      { capture: true }
    );
    req.addEventListener('input', () => {
      this.updateRecap();
      if (req.value.trim() === '') return;
      req.removeAttribute('aria-invalid');
      this.hideError();
    });
  }

  // ── 5b · Scelte obbligatorie (radio senza default): orientamento asimmetrico, tipo di taglio, ecc. ──
  // Ogni gruppo = i radio che condividono lo stesso name (properties[...]). Blocca l'add finché
  // ogni gruppo non ha una scelta. Guidato dal markup (data-sx-choice), aggiungerne altri è zero-JS.
  bindRequiredChoices() {
    const radios = Array.from(this.querySelectorAll('[data-sx-choice]'));
    if (radios.length === 0 || !this.form) return;
    const groups = {};
    radios.forEach((r) => {
      (groups[r.name] = groups[r.name] || []).push(r);
    });
    const labelOf = (name) => {
      const m = /properties\[(.+)\]/.exec(name || '');
      return m ? m[1] : 'la scelta';
    };
    // Considera SOLO i radio realmente utilizzabili: quelli dentro un pannello nascosto
    // (o disabilitati da setGroupState) non devono mai bloccare l'aggiunta al carrello,
    // altrimenti una scelta invisibile rende il prodotto non acquistabile.
    const usable = (r) => !r.disabled && !r.closest('[hidden]') && r.offsetParent !== null;
    const firstMissing = () => {
      for (const name of Object.keys(groups)) {
        const active = groups[name].filter(usable);
        if (active.length === 0) continue;
        if (!active.some((r) => r.checked)) return active[0];
      }
      return null;
    };
    this.form.addEventListener(
      'submit',
      (event) => {
        const miss = firstMissing();
        if (!miss) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Scegli «' + labelOf(miss.name) + '» prima di aggiungere al carrello.');
        const field = miss.closest('[data-sx-choice-field]');
        if (field) field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        miss.focus();
      },
      { capture: true }
    );
    radios.forEach((r) =>
      r.addEventListener('change', () => {
        this.hideError();
        this.updateRecap();
      })
    );
  }

  // ── 5c · "Sagoma personalizzata" (cut_types) → il file diventa obbligatorio ──
  // Per una forma su misura serve il tracciato/forma del cliente: blocca l'add al
  // carrello finché non c'è un file o un link, e in caso mostra il pannello file.
  bindCustomShapeUpload() {
    if (!this.form) return;
    const cutRadios = Array.from(
      this.querySelectorAll('input[name="properties[Tipo di taglio]"][data-sx-choice]')
    );
    if (cutRadios.length === 0) return;
    const isCustomShape = () => {
      const c = cutRadios.find((r) => r.checked);
      return !!c && /sagoma personalizzata/i.test(c.value);
    };
    const hasFile = () => {
      const f = this.querySelector('[data-sx-file]');
      if (f && f.files && f.files.length > 0) return true;
      const link = this.querySelector('input[name="properties[Link file]"]');
      return !!(link && link.value.trim() !== '');
    };
    const optTag = this.querySelector('[data-sx-panel="file"] .sx-field__opt, [data-sx-panel="file"] .sx-field__req');
    const syncHint = () => {
      if (!optTag) return;
      const custom = isCustomShape();
      optTag.textContent = custom ? 'obbligatorio' : 'opzionale';
      optTag.classList.toggle('sx-field__req', custom);
      optTag.classList.toggle('sx-field__opt', !custom);
    };
    this.form.addEventListener(
      'submit',
      (event) => {
        if (!isCustomShape() || hasFile()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showError('Per la «Sagoma personalizzata» carica il file della tua forma (o incolla un link) prima di aggiungere al carrello.');
        const panel = this.querySelector('[data-sx-panel="file"]');
        if (panel) panel.hidden = false;
        const f = this.querySelector('[data-sx-file]');
        if (f) {
          f.scrollIntoView({ behavior: 'smooth', block: 'center' });
          f.focus();
        }
      },
      { capture: true }
    );
    cutRadios.forEach((r) =>
      r.addEventListener('change', () => {
        this.hideError();
        syncHint();
        syncValidity();
        // Scelta appena fatta e niente file: si apre subito il pannello dove caricarlo.
        if (isCustomShape() && !hasFile()) {
          const panel = this.querySelector('[data-sx-panel="file"]');
          if (panel) panel.hidden = false;
        }
      })
    );
    // Il blocco vero è la validazione nativa sulla scelta stessa, che è sempre visibile:
    // il listener submit qui sopra arriva dopo che il tema ha già aggiunto al carrello.
    const syncValidity = () => {
      cutRadios.forEach((r) => r.setCustomValidity(''));
      const scelto = cutRadios.find((r) => r.checked);
      if (scelto && isCustomShape() && !hasFile()) {
        scelto.setCustomValidity('Per la «Sagoma personalizzata» carica il file della tua forma (o incolla un link) prima di aggiungere al carrello.');
      }
    };
    this.addEventListener('change', syncValidity);
    this.addEventListener('input', syncValidity);
    syncHint();
    syncValidity();
  }

  // ── 5d · Calcolatore al m² (sell_by_area): base × altezza → quantità (0,1 m²) ──
  // Non altera il prezzo: imposta solo la QUANTITÀ di sotto-unità "0,1 m²" in un input
  // name="quantity" associato al form (form=), così entra nel `new FormData(form)` nativo
  // di Horizon. Shopify moltiplica prezzo-variante × quantità (il totale mostrato è
  // coerente = area fatturata × €/m²). L'add è bloccato finché base e altezza non
  // rientrano nei vincoli del rotolo. Nessun add custom → upload file e carrello nativi.
  bindAreaCalculator() {
    if (this.dataset.area !== '1' || !this.form) return;
    const root = this.querySelector('[data-sx-area]');
    if (!root) return;
    const baseIn = root.querySelector('[data-sx-area-base]');
    const heightIn = root.querySelector('[data-sx-area-height]');
    if (!baseIn || !heightIn) return;
    const out = root.querySelector('[data-sx-area-out]');
    const errBox = root.querySelector('[data-sx-area-err]');
    const dimEl = root.querySelector('[data-sx-area-dim]');
    const sqmEl = root.querySelector('[data-sx-area-sqm]');
    const totalEl = root.querySelector('[data-sx-area-total]');
    const qtyInput = root.querySelector('[data-sx-area-qty]');
    const propDim = root.querySelector('[data-sx-area-prop-dim]');
    const propSqm = root.querySelector('[data-sx-area-prop-sqm]');
    const pezziIn = root.querySelector('[data-sx-area-pezzi]');
    const propPezzi = root.querySelector('[data-sx-area-prop-pezzi]');
    const pezziRow = root.querySelector('[data-sx-area-pezzi-row]');
    const pezziOut = root.querySelector('[data-sx-area-pezzi-out]');
    // Copie uguali (stessa misura, stessa grafica): 1–99, tutto il resto torna a 1.
    const pezzi = () => {
      const n = parseInt((pezziIn && pezziIn.value) || '1', 10);
      return n >= 1 ? Math.min(n, 99) : 1;
    };
    const preview = root.querySelector('[data-sx-area-preview]');
    const rectEl = root.querySelector('[data-sx-area-rect]');
    const lblB = root.querySelector('[data-sx-area-lbl-b]');
    const lblH = root.querySelector('[data-sx-area-lbl-h]');
    const meterVal = root.querySelector('[data-sx-area-meter-val]');
    const meterFill = root.querySelector('[data-sx-area-meter-fill]');
    // Tondo (custom.area_round): solo il diametro, fatturato come il quadrato diametro × diametro.
    const shapeRadios = Array.from(root.querySelectorAll('[data-sx-area-shape]'));
    const diamIn = root.querySelector('[data-sx-area-diameter]');
    const roundBox = root.querySelector('[data-sx-area-round]');
    const rectFields = Array.from(root.querySelectorAll('[data-sx-area-rectfields]'));
    const isRound = () => !!diamIn && shapeRadios.some((r) => r.checked && r.value === 'Tondo');

    const rate = parseFloat(this.dataset.areaRate) || 0; // €/m²
    const shortMax = parseInt(this.dataset.areaShort, 10) || 0; // cm (larghezza rotolo)
    const longMax = parseInt(this.dataset.areaLong, 10) || 0; // cm (lunghezza rotolo)
    const UNIT = 0.1; // m² per sotto-unità (variante)
    const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
    const nf2 = new Intl.NumberFormat('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const mFmt = (cm) => (cm % 100 === 0 ? String(cm / 100) : nf2.format(cm / 100));

    this._areaValid = false;
    this._areaRecap = '';
    this._areaPezzi = 1;

    const reset = () => {
      this._areaValid = false;
      this._areaRecap = '';
      this._areaLato = 0;
      this._areaDim = '';
      this._areaPezzi = 1;
      if (out) out.hidden = true;
      if (qtyInput) qtyInput.value = '1';
      if (propDim) { propDim.value = ''; propDim.disabled = true; }
      if (propSqm) { propSqm.value = ''; propSqm.disabled = true; }
      if (propPezzi) { propPezzi.value = ''; propPezzi.disabled = true; }
    };

    // Anteprima proporzioni: rettangolo che scala con base×altezza + metro della larghezza
    // usata sul rotolo (rosso quando un lato supera il limite). Puramente visiva.
    const STAGE = 130; // px lato massimo dello stage
    const drawPreview = (b, h, over) => {
      if (!preview || !rectEl) return;
      const lo = Math.min(b, h);
      let w, ht;
      if (b >= h) { w = STAGE; ht = STAGE * (h / b); } else { ht = STAGE; w = STAGE * (b / h); }
      rectEl.style.width = Math.max(16, Math.round(w)) + 'px';
      rectEl.style.height = Math.max(16, Math.round(ht)) + 'px';
      rectEl.classList.toggle('is-over', !!over);
      rectEl.classList.toggle('is-round', isRound());
      if (lblB) lblB.textContent = b + ' cm';
      if (lblH) lblH.textContent = h + ' cm';
      if (meterFill) {
        meterFill.style.width = Math.max(3, Math.min(100, (lo / shortMax) * 100)) + '%';
        meterFill.classList.toggle('is-over', lo > shortMax);
      }
      if (meterVal) meterVal.textContent = lo + ' / ' + shortMax + ' cm';
      preview.hidden = false;
    };
    const hidePreview = () => { if (preview) preview.hidden = true; };

    // Un campo nascosto non deve restare `required`: bloccherebbe l'invio in silenzio.
    const applyShape = () => {
      const round = isRound();
      if (roundBox) roundBox.hidden = !round;
      rectFields.forEach((el) => { el.hidden = round; });
      if (diamIn) { diamIn.disabled = !round; diamIn.required = round; }
      baseIn.disabled = round; baseIn.required = !round;
      heightIn.disabled = round; heightIn.required = !round;
    };

    // Il blocco vero è la validazione nativa: il tema aggiunge al carrello prima che il
    // submit arrivi qui (vedi bindRequiredScope). Un errore del calcolatore che non sta
    // in setCustomValidity lascia passare il prodotto a 0,1 m² e senza misure.
    const campoErrore = () => (isRound() ? diamIn : baseIn);
    const segnala = (msg) => {
      [baseIn, heightIn, diamIn].forEach((el) => el && el.setCustomValidity(''));
      if (msg) campoErrore().setCustomValidity(msg);
    };

    const compute = () => {
      const round = isRound();
      const num = (el) => Math.round(parseFloat(((el && el.value) || '').replace(',', '.')));
      const d = round ? num(diamIn) : NaN;
      const b = round ? d : num(baseIn);
      const h = round ? d : num(heightIn);
      if (!(b > 0) || !(h > 0)) {
        segnala('');
        if (errBox) errBox.hidden = true;
        hidePreview();
        reset();
        this.updateRecap();
        return;
      }
      const lo = Math.min(b, h);
      const hi = Math.max(b, h);
      let err = '';
      if (round && d > shortMax) {
        err = 'Il diametro può arrivare a ' + shortMax + ' cm (è la larghezza del rotolo). Riduci la misura.';
      } else if (hi > longMax) {
        err = 'Il lato più lungo può arrivare a ' + longMax + ' cm (' + mFmt(longMax) + ' m). Riduci la misura.';
      } else if (lo > shortMax) {
        err = 'Almeno un lato deve stare entro ' + shortMax + ' cm (è la larghezza del rotolo): ora entrambi la superano.';
      }
      drawPreview(b, h, err !== '');
      segnala(err);
      if (err) {
        if (errBox) { errBox.textContent = err; errBox.hidden = false; }
        reset();
        this.updateRecap();
        return;
      }
      if (errBox) errBox.hidden = true;

      const areaExact = (b / 100) * (h / 100);
      const units = Math.max(1, Math.ceil(areaExact / UNIT - 1e-9));
      const areaBilled = units * UNIT;
      const n = pezzi();
      const total = units * n * (rate * UNIT);
      const dimStr = round ? 'Tondo Ø' + d + ' cm' : b + ' × ' + h + ' cm';
      const sqmStr = nf2.format(areaBilled) + ' m²';

      if (dimEl) dimEl.textContent = dimStr;
      if (sqmEl) sqmEl.textContent = sqmStr;
      if (totalEl) totalEl.textContent = eur.format(total);
      if (out) out.hidden = false;
      // Superficie e Dimensioni restano quelle di UN pezzo: e' cio' che si stampa.
      if (qtyInput) qtyInput.value = String(units * n);
      if (pezziRow) pezziRow.hidden = n === 1;
      if (pezziOut) pezziOut.textContent = '\u00d7 ' + n;
      if (propPezzi) { propPezzi.value = n > 1 ? String(n) : ''; propPezzi.disabled = n === 1; }
      if (propDim) { propDim.value = dimStr; propDim.disabled = false; }
      if (propSqm) {
        propSqm.value = round
          ? sqmStr + ' (fatturata come quadrato ' + d + ' × ' + d + ' cm)'
          : sqmStr + ' (richiesta ' + nf2.format(areaExact) + ' m²)';
        propSqm.disabled = false;
      }

      this._areaValid = true;
      this._areaLato = lo;
      this._areaDim = dimStr;
      this._areaPezzi = n;
      this._areaRecap = (n > 1 ? n + ' pezzi da ' : '') + dimStr + ' · ' + sqmStr + ' · ' + eur.format(total);
      this.updateRecap();
    };

    baseIn.addEventListener('input', compute);
    heightIn.addEventListener('input', compute);
    if (diamIn) diamIn.addEventListener('input', compute);
    if (pezziIn) pezziIn.addEventListener('input', compute);
    shapeRadios.forEach((r) =>
      r.addEventListener('change', () => {
        applyShape();
        compute();
      })
    );

    this.form.addEventListener(
      'submit',
      (event) => {
        compute();
        if (this._areaValid) return; // valido → l'add nativo procede (quantity impostata)
        event.preventDefault();
        event.stopImmediatePropagation();
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (isRound()) {
          if (!(parseFloat((diamIn.value || '').replace(',', '.')) > 0) && errBox) {
            errBox.textContent = 'Inserisci il diametro (in cm) per calcolare la misura e il prezzo.';
            errBox.hidden = false;
          }
          diamIn.focus();
          return;
        }
        const b = parseFloat((baseIn.value || '').replace(',', '.'));
        const h = parseFloat((heightIn.value || '').replace(',', '.'));
        if ((!(b > 0) || !(h > 0)) && errBox) {
          errBox.textContent = 'Inserisci base e altezza (in cm) per calcolare la misura e il prezzo.';
          errBox.hidden = false;
        }
        (baseIn.value ? heightIn : baseIn).focus();
      },
      { capture: true }
    );

    applyShape();
    compute();
  }

  // ── 6 · Riepilogo ─────────────────────────────────────────────────────────

  updateRecap() {
    const recap = this.querySelector('[data-sx-recap]');
    if (!recap) return;
    const parts = [];

    if (this._areaRecap) parts.push(this._areaRecap);

    if (this.mode === 'bianco') {
      if (this.querySelector('[data-sx-purchase]')) {
        parts.push(this.adhesiveChosen ? 'Bianco + stampa adesiva personalizzata' : 'Solo bianco — struttura riutilizzabile');
        if (this.adhesiveChosen) {
          const qty = this.adhesiveQty;
          if (qty > 1) parts.push(`${qty} stampe adesive`);
        }
      } else {
        parts.push('Solo bianco — struttura riutilizzabile');
      }
    }

    this.querySelectorAll('[data-sx-choice]:checked').forEach((r) => {
      const m = /properties\[(.+)\]/.exec(r.name || '');
      // Il tema ha la sua riga piu' sotto, che tiene conto del pannello spento: qui
      // compariva due volte («Tema: Luca Savana · Tema: Luca Savana»).
      if (m && m[1] !== 'Tema') parts.push(`${m[1]}: ${r.value}`);
    });

    const designSelect = this.librarySelect('design');
    if (designSelect && !designSelect.disabled && designSelect.value) {
      parts.push(`Grafica: ${designSelect.value}`);
    }
    // Sagoma: il soggetto arriva dal PICKER (hidden canonico [data-sx-sag-value]), non più dal select libreria.
    const sagValue = this.querySelector('[data-sx-sag-value]');
    if (this.mode === 'sagoma' && sagValue && sagValue.value.trim() !== '') {
      parts.push(`Soggetto: ${sagValue.value.trim()}`);
    }
    const patternSelect = this.librarySelect('pattern');
    if (patternSelect && !patternSelect.disabled && patternSelect.value) {
      parts.push(`Pattern: ${patternSelect.value}`);
    }
    const themeInput = this.querySelector('[data-sx-panel="theme"] input[name="properties[Tema]"]:checked');
    if (themeInput && !themeInput.disabled) parts.push(`Tema: ${themeInput.value}`);

    const fileInput = this.querySelector('[data-sx-file]');
    const file = fileInput && !fileInput.disabled && fileInput.files && fileInput.files[0];
    if (file) parts.push(`File: ${file.name}`);

    const req = this.querySelector('[data-sx-required]');
    if (req && req.value.trim() !== '') parts.push(`Testo: “${req.value.trim()}”`);

    const addons = Array.from(this.querySelectorAll('[data-sx-addon]:checked'))
      .map((el) => el.dataset.sxAddonTitle)
      .filter(Boolean);
    if (addons.length > 0) parts.push(`Aggiunte: ${addons.join(' · ')}`);

    if (parts.length === 0) {
      recap.hidden = true;
      recap.textContent = '';
    } else {
      recap.textContent = `Riepilogo: ${parts.join(' · ')}`;
      recap.hidden = false;
    }
  }

  // ── 7 · Companion add (righe extra): rivestimento (taglia abbinata) + add-on HD ──

  get adhesiveQty() {
    const input = this.querySelector('[data-sx-adhesive-qty]');
    const n = input ? parseInt(input.value, 10) : 1;
    if (Number.isNaN(n) || n < 1) return 1;
    return Math.min(n, 10);
  }

  bindCompanionAdd() {
    if (this._companionBound) return; // evita doppio bind se connectedCallback rientra
    let map = null;
    try {
      map = this.dataset.adhesiveMap ? JSON.parse(this.dataset.adhesiveMap) : null;
    } catch (e) {
      map = null;
    }
    const hdVariant = this.dataset.hdVariant || null;
    // Aggiunte facoltative (metafield custom.addons): una casella per prodotto, ciascuna
    // con l'id della propria variante addosso. Nessun JSON in un attributo — una graffa
    // dentro un tag di output e' proprio cio' che Shopify rifiuta in silenzio, e questo
    // elenco cresce ogni volta che il titolare aggiunge un'aggiunta.
    const addonInputs = Array.from(this.querySelectorAll('[data-sx-addon]'));
    const tubeShort = this.dataset.area === '1' ? this.dataset.tubeShort || null : null;
    const tubeLong = this.dataset.tubeLong || null;
    if ((!map && !hdVariant && addonInputs.length === 0 && !tubeShort) || !this.form) return;
    this._companionBound = true;

    const qtyInput = this.querySelector('[data-sx-adhesive-qty]');
    if (qtyInput) qtyInput.addEventListener('change', () => this.updateRecap());
    addonInputs.forEach((el) => el.addEventListener('change', () => this.updateRecap()));

    // Le caselle (aggiunte e file grafici) scrivono una proprieta' sulla riga
    // principale: e' la traccia per l'operatore e la rete quando il JS non parte.
    // Con JS attivo pero' la stessa aggiunta diventa una riga separata col suo
    // prezzo, e nel carrello la stessa cosa si leggeva due volte — sembrava un
    // doppio addebito. Qui, a JS vivo, la proprieta' prende l'underscore iniziale
    // (il tema la nasconde ma resta nell'ordine per chi produce); senza JS il nome
    // resta visibile ed e' l'unica traccia, perche' la riga separata non nasce.
    const hdCheck = this.querySelector('[data-sx-hd]');
    const daNascondere = hdCheck ? addonInputs.concat([hdCheck]) : addonInputs;
    daNascondere.forEach((el) => {
      if (el.name && el.name.indexOf('properties[') === 0 && el.name.indexOf('properties[_') !== 0) {
        el.name = el.name.replace('properties[', 'properties[_');
      }
    });

    // Quando il tema costruisce la FormData dell'add (product-form.js: `new FormData`
    // → evento `formdata`, NON `submit`) raccogliamo le righe companion da aggiungere
    // subito dopo che il prodotto principale è entrato nel carrello.
    this.form.addEventListener('formdata', (event) => {
      const companions = [];

      // (a) Rivestimento — solo se scelto sul bianco; taglia abbinata a quella scelta
      // (il product form invia sempre `id` = variante selezionata).
      if (map && this.adhesiveChosen) {
        const idField = this.form.querySelector('[name="id"]');
        const biancoId = idField ? String(idField.value).trim() : '';
        const rid = biancoId ? map[biancoId] : null;
        if (rid) {
          companions.push({
            id: Number(rid),
            quantity: this.adhesiveQty,
            properties: {
              'Per struttura': this.dataset.productTitle || '',
              'Nota': 'Rivestimento stampato — grafica e misura sulla riga della struttura abbinata',
              '_Abbinata a prodotto': this.dataset.productTitle || '',
            },
          });
        }
      }

      // (b) Add-on file grafici — su qualsiasi prodotto con grafica, se la checkbox è on.
      const hdCheck = this.querySelector('[data-sx-hd]');
      if (hdVariant && hdCheck && hdCheck.checked) {
        companions.push({
          id: Number(hdVariant),
          quantity: 1,
          properties: {
            'Per prodotto': this.dataset.productTitle || '',
            'Nota': 'Invio dei file grafici sorgente in alta risoluzione, da riutilizzare',
            '_Add-on': 'File grafici',
          },
        });
      }

      // (c) Aggiunte facoltative — una riga per casella spuntata, col prezzo del prodotto
      // corrispondente. La quantita' resta 1: quante gliene servono lo decide il cliente
      // spuntando le voci giuste, e dal carrello puo' comunque alzare il numero.
      addonInputs.forEach((el) => {
        if (!el.checked) return;
        const vid = Number(el.dataset.sxAddon);
        if (!vid) return;
        companions.push({
          id: vid,
          quantity: 1,
          properties: {
            'Per prodotto': this.dataset.productTitle || '',
            '_Aggiunta': el.dataset.sxAddonTitle || 'Aggiunta',
          },
        });
      });

      // (d) Imballo in tubo — ogni riga al m² viaggia arrotolata in un tubo lungo quanto
      // il lato corto + 5 cm: fino a 120 cm, oppure oltre (supplemento fuori sagoma).
      // È il collo che il corriere fattura, a 0 € per il cliente. Riga e tubo si
      // riconoscono da un codice comune (`_Tubo` / `_Tubo per`): se il cliente toglie la
      // riga, il carrello toglie anche il tubo (assets/sagomax-tubi.js).
      if (tubeShort && tubeLong && this._areaValid && this._areaLato > 0) {
        const codice = 'T' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        event.formData.set('properties[_Tubo]', codice);
        // Un tubo per pezzo, anche quando i pezzi sono uguali (scelta del titolare).
        companions.push({
          id: Number(this._areaLato + 5 <= 120 ? tubeShort : tubeLong),
          quantity: this._areaPezzi || 1,
          properties: {
            'Per prodotto': this.dataset.productTitle || '',
            'Misura': this._areaDim || '',
            '_Tubo per': codice,
          },
        });
      }

      this.pendingCompanions = companions;
    });

    this.onCartLinesUpdate = (event) => {
      if (!this.pendingCompanions || this.pendingCompanions.length === 0) return;
      if (event.action !== 'add') return;
      const source = event.detail?.source;
      if (source === SagoMaxConfigurator.COMPANION_SOURCE) return;
      const companions = this.pendingCompanions;
      this.pendingCompanions = [];

      const settle = event.promise ? event.promise : Promise.resolve({ detail: {} });
      settle
        .then(({ detail }) => {
          if (detail?.didError) return; // il prodotto principale non è entrato: nessun companion
          return this.addCompanions(companions);
        })
        .catch(() => {
          /* l'add del prodotto principale è fallito: nessun companion */
        });
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartLinesUpdate);
  }

  async addCompanions(items) {
    if (!items || items.length === 0) return;
    // Dedup: se un'altra istanza/listener ha già aggiunto i companion per questo add
    // (entro pochi secondi), non ri-aggiungere — evita quantità moltiplicate.
    const now = Date.now();
    if (now - SagoMaxConfigurator._lastCompanionAddAt < 3000) return;
    SagoMaxConfigurator._lastCompanionAddAt = now;

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    const payload = { items, sections: sectionIds.join(',') };
    const totalQty = items.reduce((n, i) => n + (i.quantity || 1), 0);

    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const parsed = await response.json();
      if (parsed.status) {
        this.showError('Il prodotto è nel carrello, ma un extra (rivestimento, file grafici o aggiunte) non è stato aggiunto. Riprova o contattaci.');
        return;
      }

      // Notifica il tema col suo stesso contratto eventi: drawer e icona carrello
      // si aggiornano dalle sections restituite dall'add.
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
          promise: deferred.promise,
        })
      );

      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const ajaxCart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          sections: parsed.sections,
          source: SagoMaxConfigurator.COMPANION_SOURCE,
          itemCount: totalQty,
          didError: false,
        },
      });
    } catch (error) {
      console.error('[sagomax-configurator] companion add failed', error);
      this.showError('Il prodotto è nel carrello, ma un extra (rivestimento, file grafici o aggiunte) non è stato aggiunto. Riprova o contattaci.');
    }
  }
}

if (!customElements.get('sagomax-configurator')) {
  customElements.define('sagomax-configurator', SagoMaxConfigurator);
}
